import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import React, { useCallback, useState } from 'react'

/**
 * True when the element (or any descendant) is horizontally truncated —
 * i.e. its content is wider than the box it is clipped to. Covers both the
 * plain `text-overflow: ellipsis` spans and the marquee-style tracks whose
 * inner element overflows its viewport.
 */
export const hasTruncatedText = (element: HTMLElement): boolean => {
  if (element.scrollWidth > element.clientWidth) return true
  for (const child of Array.from(element.children)) {
    if (child instanceof HTMLElement && hasTruncatedText(child)) return true
  }
  return false
}

interface TruncationTooltipProps {
  label: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  style?: React.CSSProperties
}

/**
 * `ChartTooltip` for truncatable text: same design-system surface, but the
 * tooltip only appears when the wrapped text is actually cut off. Fully
 * visible text never gets a redundant tooltip.
 *
 * Truncation is re-measured on every mouseenter (the same approach as libra's
 * `TruncatedTitle`), so no resize listeners are needed and the answer is
 * always current for the layout the user is hovering.
 */
export const TruncationTooltip: React.FC<TruncationTooltipProps> = ({
  label,
  children,
  side = 'top',
  style,
}) => {
  const [isTruncated, setIsTruncated] = useState(false)

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setIsTruncated(hasTruncatedText(event.currentTarget))
    },
    []
  )

  if (!label) return <>{children}</>

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          style={{ display: 'inline-flex', minWidth: 0, ...style }}
          onMouseEnter={handleMouseEnter}>
          {children}
        </span>
      </TooltipTrigger>
      {isTruncated && <TooltipContent side={side}>{label}</TooltipContent>}
    </Tooltip>
  )
}
